import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mvpApi } from '../../../services/api';

function dateLocaleFromI18n(lang) {
  const l = (lang || 'en').toLowerCase();
  if (l.startsWith('es')) return 'es-ES';
  if (l.startsWith('en')) return 'en-GB';
  return lang || undefined;
}

/**
 * Two-step flow: confirm (draft vs final messaging) → success with download (Readdy-style EX-10).
 */
export default function GenerateFormModal({
  open,
  onClose,
  isDraft,
  caseDisplayId,
  applicantName,
  identityPairs,
  contactPairs,
  addressPairs,
  representativePairs,
  notificationPairs,
  formCode = 'EX-10',
  formSubtitle = 'Solicitud de autorización, circunstancias excepcionales',
  languageLabel = 'Spanish (official)',
  firstName = '',
  lastName = '',
  secondSurname = '',
  userId,
  sessionId,
}) {
  const { i18n } = useTranslation();
  const [step, setStep] = useState('confirm');
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [filename, setFilename] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const renameFilenameWithFormCode = (name, code) => {
    const safeCode = String(code || '').trim().replace(/\s+/g, '-');
    if (!safeCode) return name;
    const n = String(name || '').trim();
    const m = n.match(/^(.*?)(\.pdf)$/i);
    if (!m) return `${n}_${safeCode}`;
    return `${m[1]}_${safeCode}${m[2]}`;
  };

  useEffect(() => {
    if (!open) return;
    setStep('confirm');
    setDownloadUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setFilename('');
  }, [open]);

  useEffect(
    () => () => {
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    },
    [downloadUrl],
  );

  const handleGenerate = useCallback(async () => {
    if (!userId || !sessionId || isGenerating) return;
    setIsGenerating(true);
    try {
      const resp = await mvpApi.getAdminExFormPreview(userId, sessionId, formCode, false);
      const blob =
        resp?.data instanceof Blob ? resp.data : new Blob([resp?.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setDownloadUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      const cd = String(resp?.headers?.['content-disposition'] || '');
      const match = cd.match(/filename=\"?([^\";]+)\"?/i);
      const fallback = `${[lastName, secondSurname, firstName].filter(Boolean).join('-') || 'case'}.pdf`;
      setFilename(renameFilenameWithFormCode((match?.[1] || fallback).trim(), formCode));
      setStep('success');
    } catch (e) {
      console.error('Generate form failed', e);
      window.alert(
        e?.response?.data?.detail ||
          e?.message ||
          'Could not generate the form. Check your connection and try again.',
      );
    } finally {
      setIsGenerating(false);
    }
  }, [userId, sessionId, isGenerating, formCode, lastName, secondSurname, firstName]);

  const handleDownload = useCallback(() => {
    if (!downloadUrl || !filename) return;
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [downloadUrl, filename]);

  const handlePreview = useCallback(() => {
    if (!downloadUrl) return;
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  }, [downloadUrl]);

  const handleClose = useCallback(() => {
    setDownloadUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setFilename('');
    setStep('confirm');
    onClose?.();
  }, [onClose]);

  const todayLabel = useMemo(() => {
    const loc = dateLocaleFromI18n(i18n.language);
    return new Date().toLocaleDateString(loc, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }, [i18n.language]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 p-3 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="generate-form-title"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-md max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-2xl bg-white p-4 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-slate-100 rounded-xl">
              <i className="ri-file-text-line text-slate-600 text-lg" />
            </div>
            <div className="min-w-0">
              <h2 id="generate-form-title" className="text-sm font-semibold text-slate-900 flex flex-wrap items-center gap-2">
                {`Generate ${formCode} Form`}
                {isDraft ? (
                  <span className="text-[10px] font-bold tracking-wide bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-md">
                    DRAFT
                  </span>
                ) : null}
              </h2>
              <p className="text-[11px] text-slate-400 break-words">{formSubtitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer transition-colors rounded-lg hover:bg-slate-100"
            aria-label="Close"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        {step === 'confirm' ? (
          <>
            <div className="space-y-3 mb-5">
            {isDraft ? (
                <div className="flex items-center gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className="ri-draft-line text-amber-500 text-sm" />
                  </div>
                  <p className="text-xs text-amber-700 leading-relaxed">
                  <>
                    Some case information is incomplete. The form will be generated with a <strong>DRAFT</strong>{' '}
                    watermark.
                  </>
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5">
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-checkbox-circle-line text-emerald-500 text-sm" />
                </div>
                <p className="text-xs text-emerald-700 leading-relaxed">
                  All required information is complete. The PDF will be generated without a draft watermark.
                </p>
              </div>
            )}

              <div className="bg-slate-50 rounded-xl px-4 py-3">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Form details</p>
                <div className="space-y-1.5">
                  <div className="flex items-start justify-between gap-3 text-xs">
                    <span className="text-slate-500">Form</span>
                    <span className="font-medium text-slate-800 flex flex-wrap items-center justify-end gap-1.5 text-right">
                      {formCode}
                      {isDraft ? (
                        <span className="text-[10px] font-bold tracking-wide bg-amber-100 text-amber-700 border border-amber-200 px-1 py-px rounded">
                          DRAFT
                        </span>
                      ) : null}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-xs">
                    <span className="text-slate-500">Language</span>
                    <span className="font-medium text-slate-800 text-right break-words">{languageLabel}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-xs">
                    <span className="text-slate-500">Date</span>
                    <span className="font-medium text-slate-800">{todayLabel}</span>
                  </div>
                </div>
              </div>

              <p className="text-[11px] text-slate-400 leading-relaxed">
              Data will be pulled from Identity, Address, Representative, and Notification Address sections. Missing fields will appear blank on the form.
            </p>
            </div>

            <div className="flex flex-col gap-2 min-[420px]:flex-row">
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating}
                className="min-h-[44px] flex-1 flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-download-line" />
                {isGenerating
                  ? 'Generating…'
                  : isDraft
                    ? 'Generate PDF (Draft)'
                    : 'Generate PDF'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="min-h-[44px] px-4 text-sm font-medium border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-3">
              <i className="ri-checkbox-circle-fill text-emerald-600 text-2xl" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800 mb-1">
                Form generated{isDraft ? ' (draft)' : ''}
              </p>
              <p className="text-xs text-slate-500 mb-4">
                {filename ? `${filename} ready` : 'PDF ready'}
              </p>
            </div>
            <div className="flex flex-col gap-2 justify-center min-[420px]:flex-row min-[420px]:flex-wrap">
              <button
                type="button"
                onClick={handlePreview}
                className="min-h-[44px] flex items-center justify-center gap-1.5 text-sm font-medium px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-eye-line" /> Preview {formCode}
              </button>
              <button
                type="button"
                onClick={handleDownload}
                className="min-h-[44px] flex items-center justify-center gap-1.5 text-sm font-medium px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line" /> Download {formCode}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="min-h-[44px] text-sm font-medium px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
